﻿import React, { Fragment, useEffect, useState } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import {
    ShowChart, TableChart, HorizontalSplit, Edit, Save, MoreVert, PublishOutlined, GetAppOutlined,SaveAltOutlined
} from '@mui/icons-material'
import { muiOptions, defaultColors, defaultStyles, MuiProps } from '../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import { MktSalesContainer } from './mktSalesStore'
import moment from 'moment'
import * as api from '../../infrastructure/api'
import { snackbars } from '../../infrastructure/snackbars'
import { hasFeature, FeatureContainer } from '../../infrastructure/feature'
import { Button, Menu } from '../common/customComponents'
import { hasClaim } from '../../infrastructure/signIn/userContext'
import { Claims } from '../../infrastructure/signIn/models'
import { ExportMktSalesArgs, ForecastSnapshotParam, SnapshotType, MktSaleForecastSnapshotInfo } from './models'
import { Guid } from '@guid'
import { SnapshotConfirmationDialogs } from './_snapshotDialogs'
import { SnapshotListDialog, useSnapshotListDialog } from './_snapshotListDialog'
import { useSnapshotLabel } from './useSnapshotLabel'
import { dealApi } from '../deals/dealApis'
import { UserSettingsContextContainer } from '../../infrastructure/shell/userSettings/userSettingsStore'
import { MktSalesExportFilesDialog } from './_mktSalesExportFilesDialog'
import { MktSalesFiltersContainer } from './mktSalesFiltersStore'

let _MktSalesBoardHeader = ({ classes }: MuiProps) => {
    let store = MktSalesContainer.useContainer()
    let filterStore = MktSalesFiltersContainer.useContainer()
    let settings = UserSettingsContextContainer.useContainer()
    let dateFormat = settings.getDateFormat()
    
    let [isSnapshotCancellationDialogOpen, setIsSnapshotCancellationDialogOpen] = useState<boolean>(false)
    let [isSnapshotCreationDialogOpen, setIsSnapshotCreationDialogOpen] = useState<boolean>(false)
    let [isExportFilesDialogOpen, setIsExportFilesDialogOpen] = useState<boolean>(false)
    let [snapshotType, setSnapshotType] = useState<SnapshotType | null>(null)
    let [initialSnapshots, setInitialSnapshots] = useState<MktSaleForecastSnapshotInfo[]>([])
    let [longTermPlanSnapshots, setLongTermPlanSnapshots] = useState<MktSaleForecastSnapshotInfo[]>([])

    let { getSnapshotLabel } = useSnapshotLabel()
    let snapshotListDialog = useSnapshotListDialog()

    let snapshotsOf = (type: SnapshotType) =>
        type === 'Initial' ? initialSnapshots : longTermPlanSnapshots

    let openSnapshotList = (type: SnapshotType) =>
        snapshotListDialog.open({
            snapshotType: type,
            snapshots: snapshotsOf(type),
            displayCompanyColumn: filterStore.companiesFilter.choices.length > 1,
            getCompanyName: companyCode =>
                filterStore.companiesFilter.items.find(company => company.code === companyCode)?.name
                ?? companyCode,
            onSnapshotDeleted: async () => {
                await store.loadMktSaleMovements()
                await loadExistingForecastSnapshot(type)
            }
        })

    let openForecastPopup = () => store.setShowForecastPopup(true)
    let openExportFilesDialog = () => setIsExportFilesDialogOpen(true)
    let hideExportFilesDialog = () => setIsExportFilesDialogOpen(false)

    let showCreationDialog = (type: SnapshotType) => {
        setSnapshotType(type)
        setIsSnapshotCreationDialogOpen(true)
    }

    let showCancellationDialog = (type: SnapshotType) => {
        setSnapshotType(type)
        setIsSnapshotCancellationDialogOpen(true)
    }

    let hideCreationDialog = () => {
        setSnapshotType(null)
        setIsSnapshotCreationDialogOpen(false)
    }

    let hideCancellationDialog = () => {
        setSnapshotType(null)
        setIsSnapshotCancellationDialogOpen(false)
    }

    let isManager = hasClaim(Claims.SalesManager)

    let uploadActualFile = async (e) => {
        await uploadFile('stock/movement/mktsale/import/actualMktSales', e)
        snackbars.success(t('httpSuccess.actualSalesImported'))
    }

    let uploadForecastFile = async (e) => {
        try {
            await uploadFile('stock/movement/mktsale/import/forecastMktSales', e)
        }
        catch (e) {
            if (e.status == 504 && hasFeature('MktSalesForecastShipTo'))
                snackbars.info(t('mktSales.label.info.tactixOutput'))
            throw e
        }
    }

    let uploadFile = async (url: string, e) => {
        await api.upload(url, e.target.files[0], 'import')
        await store.loadMktSaleMovements()
    }

    let exportMktSales = async () => {
        let startDate = !filterStore?.startDate
            ? moment.utc().startOf('month')
            : moment(filterStore.startDate).startOf('day')

        let endDate = !filterStore?.endDate
            ? moment(startDate).endOf('month')
            : moment(filterStore.endDate).endOf('day')

        let dateFrom = moment(startDate).format('YYYYMMDD')
        let dateTo = moment(endDate).format('YYYYMMDD')
        let filename = `${dateFrom}-${dateTo}-sales.xlsx`

        let filters: ExportMktSalesArgs = {
            filename: filename,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            site: filterStore.sitesFilter.values(),
            productId: filterStore.productIdsFilter.values(),
            companies: filterStore.companiesFilter.values(),
            customerSegments: filterStore.customerSegmentsFilter.values()
        }
        snackbars.info(t('httpInfo.mktSalesExportStarted'))
        await api.post('stock/movement/mktsale/export', filters)
        pollExportStatus(filename)
    }

     let pollExportStatus = (filename: string, attempt = 0) => {
        const maxAttempts = 200 // ~10 min à 3s d'intervalle
        setTimeout(async () => {
            let status = await api.get<'Unknown' | 'Processing' | 'Ready' | 'Failed'>(
                `stock/movement/mktsale/export/status/${filename}`)
            if (status === 'Ready') {
                snackbars.success(t('httpSuccess.mktSalesExported'))
                if(!hasFeature('ExportFilesMktSales')){
                    await api.openDownload(filename)
                }
            } else if (status === 'Failed') {
                snackbars.error(t('httpErrors.mktSalesExportFailed'))
            } else if (attempt < maxAttempts) {
                pollExportStatus(filename, attempt + 1)
            } else {
                snackbars.error(t('httpErrors.timeout'))
            }
        }, 3000)
    }

    let featureStore = FeatureContainer.useContainer()

    let loadExistingForecastSnapshot = async (type: SnapshotType) => {
        if (!hasClaim(Claims.SalesManager) || (type === 'LongTermPlan' && !(await featureStore.hasFeatureAsync('MktSalesLongTermPlan'))))
            return

        let getExistingSnapshot = async (arg: Omit<ForecastSnapshotParam, 'company'>) => {
            var snapshots = await Promise.all(
                filterStore.companiesFilter.values().map(company =>
                    getForecastSnapshotInfo({ ...arg, company })))
            switch (type) {
                case 'Initial':
                    setInitialSnapshots(snapshots.flat())
                    break
                case 'LongTermPlan':
                    setLongTermPlanSnapshots(snapshots.flat())
                    break
            }
        }
        
        let selectedCompanies = filterStore.companiesFilter.values()

        if (!!selectedCompanies.length && !!filterStore.startDate) {
            let params = getForecastSnapshotParamWithoutCompany(type)
            getExistingSnapshot(params)
        }
    }

    useEffect(() => { loadExistingForecastSnapshot('Initial') }, [filterStore.selectedValues, store.showForecastPopup])
    useEffect(() => { loadExistingForecastSnapshot('LongTermPlan') }, [filterStore.selectedValues, store.showForecastPopup])

    let getForecastSnapshotInfo = async (arg: ForecastSnapshotParam) =>
        await api.get<MktSaleForecastSnapshotInfo[]>(`stock/movement/mktsale/forecastSnapshot/${arg.company}/${arg.start}/${arg.end}/${arg.type}`)

    let getForecastSnapshotParamWithoutCompany = (type: SnapshotType): Omit<ForecastSnapshotParam, 'company'> => {
        let start = moment(filterStore.startDate).format('yyyy-MM-DD')
        let end = moment(filterStore.endDate).format('yyyy-MM-DD')

        return {
            start: start,
            end: end,
            type
        }
    }

    let createSnapshot = async (type: SnapshotType) => {
        let params = getForecastSnapshotParamWithoutCompany(type)
        await Promise.all(
            filterStore.companiesFilter.values().map(company => {
                let companyParams: ForecastSnapshotParam = {
                    company,
                    start: params.start,
                    end: params.end,
                    type: params.type
                }
                return api.post<ForecastSnapshotParam>('stock/movement/mktsale/snapshot', companyParams)
            }))
        snackbars.success(t('mktSales.forecast.saveForecastSuccess', { type: getSnapshotLabel(type) }))
        hideCreationDialog()

        await store.loadMktSaleMovements()
        loadExistingForecastSnapshot(type)
    }

    let cancelSnapshot = async (type: SnapshotType) => {
        await api.del(`stock/movement/mktsale/snapshot/${snapshotsOf(type)[0].id}`)
        snackbars.success(t('mktSales.forecast.cancelForecastSuccess', { type: getSnapshotLabel(type) }))
        hideCancellationDialog()

        await store.loadMktSaleMovements()
        loadExistingForecastSnapshot(type)
    }

    let refreshMktSalesDeals = async () => {
        await dealApi.refreshInternalDealsFromMktSales(false)
        snackbars.success(t('mktSales.internalDeal.operationCompleted'))
    }

    let snapshotTypes: SnapshotType[] = hasFeature('MktSalesLongTermPlan') ? ['Initial', 'LongTermPlan'] : ['Initial']

    return (
        <>
            <div className={classes.container}>
                <div className={classes.paperHeader}>
                    <div className={classes.modeSelectors}>
                        <ToggleButtonGroup
                            value={store.displayMode}
                            exclusive
                            onChange={(_, newValue) => { if (newValue) store.setDisplayMode(newValue) }}>
                            <Tooltip title={<Typography variant='body1'>{t('mktSales.label.splitMode')}</Typography>} placement='top'>
                                <ToggleButton value='split' selected={store.displayMode == 'split'} classes={{ selected: classes.selected }}>
                                    <HorizontalSplit />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title={<Typography variant='body1'>{t('mktSales.label.graphMode')}</Typography>} placement='top'>
                                <ToggleButton value='graph' selected={store.displayMode == 'graph'} classes={{ selected: classes.selected }}>
                                    <ShowChart />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title={<Typography variant='body1'>{t('mktSales.label.tableMode')}</Typography>} placement='top'>
                                <ToggleButton value='table' selected={store.displayMode == 'table'} classes={{ selected: classes.selected }}>
                                    <TableChart />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    </div>
                    <div className={classes.rightButtonsContainer}>
                        {isManager && hasFeature('MktSalesDeals')
                            && <Button onClick={refreshMktSalesDeals}
                                label={t('mktSales.internalDeal.refreshButton')}
                                className={classes.refreshDealButton} />
                        }
                        {snapshotTypes.map(type => {
                            let snapshots = snapshotsOf(type)
                            return (
                                <Fragment key={type}>
                                    {snapshots.length === 0 && isManager &&
                                        <Button onClick={() => showCreationDialog(type)}
                                            label={t('mktSales.forecast.saveForecast', { type: getSnapshotLabel(type) })}
                                            className={classes.secondaryButton} />
                                    }
                                    {snapshots.length === 1 &&
                                        <>
                                            <Typography variant='h6' className={classes.snapshotText}>
                                                {t('mktSales.forecast.existingSnapshot', { type: getSnapshotLabel(type) })
                                                    + moment(snapshots[0].createdAt).format(dateFormat)}
                                            </Typography>
                                            {isManager &&
                                                <Button onClick={() => showCancellationDialog(type)}
                                                    label={t('mktSales.forecast.cancelForecast', { type: getSnapshotLabel(type) })}
                                                    className={classes.secondaryButton} />
                                            }
                                        </>
                                    }
                                    {snapshots.length > 1 &&
                                        <Button onClick={() => openSnapshotList(type)}
                                            label={t('mktSales.forecast.snapshotsButton', { type: getSnapshotLabel(type) })}
                                            className={classes.secondaryButton} />
                                    }
                                </Fragment>)
                        })
                        }
                        {isManager && (hasFeature('MktSalesSiteVolume') || hasFeature('MktSalesEditForecastVolume')) &&
                            <Button onClick={store.toggleTableEditMode}
                                img={!store.tableEditMode.isOn ? (<Edit />) : (<Save />)}
                                label={t(`trucks.table.${!store.tableEditMode.isOn ? 'edit' : 'save'}`)}
                                className={classes.editButton} />
                        }
                        {hasFeature('MktSalesForecast') &&
                            <Button onClick={openForecastPopup}
                                label={t('mktSales.table.add')}
                                className={classes.openPopupButton} />
                        }
                        <Menu
                            icon={<MoreVert />}
                            items={[...(isManager ? [{
                                text: t('mktSales.table.uploadActual'),
                                isUpload: true,
                                onUpload: uploadActualFile,
                                icon: <PublishOutlined className={classes.iconStyle} />,
                            },
                            {
                                text: t('mktSales.table.uploadForecast'),
                                isUpload: true,
                                onUpload: uploadForecastFile,
                                icon: <PublishOutlined className={classes.iconStyle} />,
                            }] : []),
                            {
                                text: t('mktSales.table.exportSales'),
                                icon: <GetAppOutlined className={classes.iconStyle} />,
                                onClick: exportMktSales,
                            },
                            ...(hasFeature('ExportFilesMktSales') ? [{
                                text: t('mktSales.table.exportFiles'),
                                icon: <SaveAltOutlined className={classes.iconStyle} />,
                                onClick: openExportFilesDialog,
                            }] : [])]}
                            classesOverride={{ button: classes.menuButton }} />
                    </div>
                </div>
            </div>
            <SnapshotConfirmationDialogs snapshotType={snapshotType}
                isSnapshotCreationDialogOpen={isSnapshotCreationDialogOpen}
                hideCreationDialog={hideCreationDialog}
                createSnapshot={createSnapshot}
                isSnapshotCancellationDialogOpen={isSnapshotCancellationDialogOpen}
                hideCancellationDialog={hideCancellationDialog}
                cancelSnapshot={cancelSnapshot}
                fullMonthSelected={store.onFullMonth()} />
            <SnapshotListDialog {...snapshotListDialog.dialogProps} />
            <MktSalesExportFilesDialog
                open={isExportFilesDialogOpen}
                onClose={hideExportFilesDialog} />
        </>
    )
}

let styles = _ =>
    createStyles({
        container: {
            height: '100%',
            width: '100%',
            paddingBottom: '-1em'
        },
        paperHeader: {
            position: 'relative',
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            margin: '0em 1em',
            marginTop: '0.5em',
            marginBottom: '-1em',
            zIndex: 3,
            userSelect: 'none'
        },
        modeSelectors: {
            display: 'flex',
            alignItems: 'center'
        },
        menuButton: {
            padding: '5px'
        },
        openPopupButton: {
            ...defaultStyles.primaryButton,
            marginRight: '0.5em'
        },
        editButton: {
            ...defaultStyles.secondaryButton,
            padding: '0px',
            cursor: 'pointer',
            marginRight: '0.8em'
        },
        iconStyle: { height: '1.5rem', width: '1.5rem', marginTop: '-0.2rem' },
        rightButtonsContainer: {
            ...defaultStyles.flexRow
        },
        selected: {
            "&&": {
                backgroundColor: defaultColors.lightBlue.main.color,
                color: 'white',
                '&:hover': {
                    backgroundColor: defaultColors.lightBlue.light.color,
                },
            }
        },
        secondaryButton: {
            ...defaultStyles.secondaryButton,
            marginRight: '0.8em'
        },
        snapshotText: {
            color: defaultColors.darkBlue.main.color,
            marginRight: '0.8em'
        },
        refreshDealButton: {
            ...defaultStyles.secondaryButton,
            marginRight: '0.8em'
        }
    })

export let MktSalesBoardHeader = withStyles(styles, muiOptions)(_MktSalesBoardHeader)